// src/utils/appNotifications.js
//
// Daily local notifications, Mon-Sat at 10:00 local time, rotating copy.
//
// iOS caps an app at 64 pending notifications, so rather than one repeating
// rule this queues the next WINDOW_SIZE Mon-Sat occurrences with different copy
// on each, and rebuilds that window whenever the app starts or resumes.
//
// Rotation comes from the day number rather than an array counter, so a rebuild
// lands on the same message that slot would have had anyway instead of resetting
// everyone to message #1 on every launch.
//
// Times are set with setHours on a local Date, which keeps 10 AM at 10 AM
// across a DST change.
//
// The one real caveat: the queue is finite. Someone who doesn't open the app for
// ~10 weeks runs it dry. Raising WINDOW_SIZE to 63 buys the maximum runway; a
// silent background refresh would remove the limit properly.

import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

const ID_BASE = 1000;
const PREVIEW_ID = 1999;   // outside the window block, so a rebuild can't swallow it
const WINDOW_SIZE = 60;    // ~10 weeks of Mon-Sat, under the iOS cap of 64
const HOUR = 10;
const MINUTE = 0;

// Opt-OUT, not opt-in. These run for anyone who granted notification
// permission; a user who never granted it simply never sees them, since
// scheduleDailyNotifications bails on a permission check rather than prompting.
// The key only exists so a settings toggle can suppress them later.
const DISABLED_KEY = 'dailyNotificationsDisabled';

// Titles under ~30 chars and bodies under ~100 so nothing truncates on either
// platform. Varying the verb keeps repeats from feeling stale — with a window
// of 60 slots, three messages would come round twenty times.
//
// Deliberately generic: these are queued weeks ahead and can't know what was
// actually published, and the app serves four countries, so nothing here names
// a place or an institution.
export const MESSAGES = [
  {
    title: 'Your government has been busy',
    body: "See what's new from official sources today.",
  },
  {
    title: 'Fresh from the source',
    body: 'New reports, filings, and updates are waiting.',
  },
  {
    title: 'Stay in the know',
    body: 'Catch up on the latest official government activity.',
  },
  {
    title: 'Your representatives at a glance',
    body: "See what they've been up to.",
  },
  {
    title: 'Take a glance',
    body: 'A minute of government news, straight from the source.',
  },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const ids = () => Array.from({ length: WINDOW_SIZE }, (_, i) => ID_BASE + i);

export const isDailyNotificationsEnabled = () => {
  try {
    return localStorage.getItem(DISABLED_KEY) !== '1';
  } catch {
    return true;
  }
};

const setEnabledFlag = (enabled) => {
  try {
    if (enabled) localStorage.removeItem(DISABLED_KEY);
    else localStorage.setItem(DISABLED_KEY, '1');
  } catch {
    // The schedule on the device is the real source of truth
  }
};

/** The next WINDOW_SIZE Mon-Sat occurrences at HOUR:MINUTE, from the next one still ahead. */
const upcomingSlots = () => {
  const slots = [];
  const cursor = new Date();
  cursor.setHours(HOUR, MINUTE, 0, 0);
  if (cursor.getTime() <= Date.now()) cursor.setDate(cursor.getDate() + 1);

  while (slots.length < WINDOW_SIZE) {
    if (cursor.getDay() !== 0) slots.push(new Date(cursor)); // 0 = Sunday
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(HOUR, MINUTE, 0, 0); // re-assert across a DST boundary
  }
  return slots;
};

const messageFor = (date) => MESSAGES[Math.floor(date.getTime() / MS_PER_DAY) % MESSAGES.length];

export const cancelDailyNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({
      notifications: [...ids(), PREVIEW_ID].map((id) => ({ id }))
    });
  } catch (error) {
    console.warn('[notifications] cancel failed', error);
  }
};

/**
 * Rebuild the whole window.
 *
 * @param {boolean} prompt - request permission if not yet granted. True when the
 *   user asked for this; false on a background refresh, which should never make
 *   a permission dialog appear out of nowhere.
 * @returns {Promise<boolean>} whether anything is now scheduled
 */
export const scheduleDailyNotifications = async ({ prompt = false } = {}) => {
  if (!Capacitor.isNativePlatform()) return false;

  let permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') {
    if (!prompt) return false;
    permission = await LocalNotifications.requestPermissions();
  }
  if (permission.display !== 'granted') return false;

  await cancelDailyNotifications();

  const notifications = upcomingSlots().map((at, index) => {
    const message = messageFor(at);
    return {
      id: ID_BASE + index,
      title: message.title,
      body: message.body,
      schedule: { at, allowWhileIdle: true },
      extra: { type: 'daily' }
    };
  });

  try {
    await LocalNotifications.schedule({ notifications });
    return true;
  } catch (error) {
    console.warn('[notifications] schedule failed', error);
    return false;
  }
};

/** Turn daily notifications on or off. Returns the state actually reached. */
export const setDailyNotificationsEnabled = async (enabled) => {
  if (!enabled) {
    await cancelDailyNotifications();
    setEnabledFlag(false);
    return false;
  }
  const scheduled = await scheduleDailyNotifications({ prompt: true });
  setEnabledFlag(scheduled);
  return scheduled;
};

/**
 * Top up the window. Safe to call on every start and resume, and this is the
 * only thing that needs calling — it schedules for anyone with permission
 * already granted, and quietly does nothing for anyone without it. No prompt is
 * ever raised from here.
 */
export const refreshDailyNotifications = async () => {
  if (!isDailyNotificationsEnabled()) return false;
  return scheduleDailyNotifications({ prompt: false });
};

/**
 * Fire one a few seconds out — the only practical way to verify delivery.
 * Uses real rotation copy rather than placeholder text, so what you see in the
 * banner is what users will actually get. Pass an index to preview a specific
 * message instead of today's.
 */
export const testNotification = async (seconds = 5, index = null) => {
  if (!Capacitor.isNativePlatform()) {
    console.warn('[notifications] not a native platform — nothing will fire');
    return false;
  }

  let permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') permission = await LocalNotifications.requestPermissions();
  if (permission.display !== 'granted') {
    console.warn('[notifications] permission denied');
    return false;
  }

  const message = index === null
    ? messageFor(new Date())
    : MESSAGES[((index % MESSAGES.length) + MESSAGES.length) % MESSAGES.length];

  await LocalNotifications.schedule({
    notifications: [{
      id: PREVIEW_ID,
      title: message.title,
      body: message.body,
      schedule: { at: new Date(Date.now() + seconds * 1000), allowWhileIdle: true },
      extra: { type: 'daily' }
    }]
  });

  console.log(`[notifications] "${message.title}" in ${seconds}s`);
  return true;
};

/** The full rotation, for checking copy without firing anything. */
export const notificationMessages = () => MESSAGES;

/**
 * Cancel every pending notification, whatever scheduled it.
 *
 * Deleting a scheduler's code doesn't unschedule what it already queued — only
 * a cancel call carrying those ids does. This sweeps ids this module doesn't
 * know about, which is what's needed after retiring one.
 */
export const clearAllNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return 0;
  const pending = await LocalNotifications.getPending();
  if (pending.notifications.length === 0) return 0;

  await LocalNotifications.cancel({
    notifications: pending.notifications.map((n) => ({ id: n.id }))
  });
  console.log(`[notifications] cleared ${pending.notifications.length}`);
  return pending.notifications.length;
};

/** What's queued right now — id, time, and copy for each slot. */
export const pendingNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return [];
  const pending = await LocalNotifications.getPending();
  return pending.notifications
    .map((n) => ({ id: n.id, at: n.schedule?.at, title: n.title, body: n.body }))
    .sort((a, b) => new Date(a.at) - new Date(b.at));
};